<template>
    <div class="flex flex-col xxs:w-full md:w-[calc(50%-10px)] lg:w-[calc(33%-10px)] xxs:h-auto md:min-h-[440px] news-card mt-4 bg-white border border-interactive-9 rounded hover:drop-shadow-[0_2px_16px_rgba(100,100,100,0.21)] hover:z-10 hover:opacity-100 hover:border-white">

        <router-link
            :to="`/resources/view/${resource.uid}`"
            v-if="hasThumbnail && isPageResource"
            class="block w-full h-auto"
        >
            <Image
                :src="thumbnail"
                type="thumbnail"
                :lazyLoading="index >= 3"
                :index="index"
            />
        </router-link>
        <a
            v-if="!isPageResource && !hasThumbnail"
            :href="formattedUrl"
            target="_blank"
            class="w-full h-auto aspect-thumbnail flex items-center justify-center relative"
        >
            <AttachmentIcon
                :color="color.hex"
            />
            <div class="w-full aspect-thumbnail absolute top-0 left-0 z-0 bg-primary-1 opacity-10"></div>
        </a>
        <a v-if="!isPageResource && hasThumbnail"
           :href="formattedUrl"
           target="_blank"
           class="block w-full h-auto">
            <Image
                :src="thumbnail"
                type="thumbnail"
                :lazyLoading="index >= 3"
                :index="index"
            />
        </a>

        <div class="w-full flex flex-col px-4 pt-4 pb-6 box-border flex-1 relative">
            <div v-if="hasUrl">
                <a :href="formattedUrl" title="" target="_blank" class="absolute top-0 left-0 w-full h-[calc(100%-45px)]"></a>
                <div class="w-full text-body-7 text-primary-1 flex items-center">
                    <UrlIcon class="w-4 h-4 inline-block" :color="color.hex" />
                    <span class="ml-2 overflow-hidden overflow-ellipsis max-h-[60px] multi-line-ellipsis line-clamp-1">{{resource.resourceable.url}}</span>
                </div>
            </div>
            <router-link
                v-else
                :to="{name: 'view-resource', params: {uid: resource.uid}}"
                title=""
                class="absolute top-0 left-0 w-full h-[calc(100%-45px)]"
            ></router-link>

            <div v-if="!hasUrl" class="w-full text-body-7 text-interactive-7 flex items-center">
                <img src="@/assets/icons/receipt.svg" alt="receipt" class="w-4 h-4 inline-block" />
                <span class="ml-2">{{resource.resourceable.resource_category.name}}</span>
            </div>

            <h1 class="text-body-5 text-default-1 w-full overflow-hidden overflow-ellipsis max-h-[66px] multi-line-ellipsis clamp-3-lines mt-3" :class="{hasThumbnail: 'mt-4'}">
                {{resource.resourceable.title}}
            </h1>

            <div v-html="processedHtml" class="mt-2 text-body-6 text-default-2 w-full overflow-hidden overflow-ellipsis max-h-[60px] multi-line-ellipsis clamp-3-lines"/>

            <div class="flex flex-1"></div>
            <div class="w-full flex mt-4" v-if="hasTags">
                <NewsTag :tags="resource.tags" />
            </div>

            <div class="flex w-full justify-between items-center mt-4">
                <div class="w-full text-body-7 text-default-4 mt-3">
                    {{formattedDate}}
                </div>
                <div class="relative ml-auto flex items-center justify-center">
                    <Favorite
                        class="mr-3"
                        :is-liked="resource.isLiked"
                        :resource-uid="resource.uid"
                        @toggle-like="toggleLike"
                    />
                    <ActionMenuNewsCard
                        @view="emit('view')"
                        :is-on-current-organization="resource.organization === organization.slug"
                        :is-trashed="isTrashed"
                        @share="emit('share', resource)"
                        @edit="emit('edit', {type: resource.resourceable_type, uid: resource.uid})"
                        @delete="emit('delete', resource)"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import {computed} from "vue";
import ActionMenuNewsCard from "@/components/custom/news/ActionMenuNewsCard.vue";
import {useAuth} from "@/stores/AuthStore";
import {storeToRefs} from "pinia";
import NewsTag from "@/components/custom/news/NewsTag.vue";
import UrlIcon from "@/components/icons/small/Url.vue";
import {useTheme} from "@/stores/ThemeStore";
import Favorite from "@/components/custom/resources/Favorite.vue";
import Image from "@/components/custom/Image.vue";
import {useFormatDate} from "@/composables/useFormatDate";
import AttachmentIcon from "@/components/icons/rounded/Attachment.vue";

const {organization} = storeToRefs(useAuth());
const {color} = useTheme();

const props = defineProps({
    resource: {
        type: Object,
        required: true,
    },
    index: {
        type: Number,
        required: true,
    }
})

const emit = defineEmits(['toggleLike', 'share', 'edit', 'view', 'delete'])

function toggleLike() {
    emit('toggleLike', props.index);
}

const isTrashed = computed(() => {
    if(!props.resource) {
        return false;
    }

    return props.resource.deleted_at !== null;
})

const hasThumbnail = computed(() => {
    if(!Object.hasOwn(props.resource.resourceable, 'thumbnail') || !props.resource.resourceable.thumbnail) {
        return false;
    }

    return props.resource.resourceable.thumbnail.length > 0;
})

const thumbnail = computed(() => {
    if(!hasThumbnail.value) {
        return '';
    }

    return props.resource.resourceable.thumbnail;
})

const formattedDate = computed(() => {
    if(!props.resource.resourceable) {
        return '';
    }

    return useFormatDate( props.resource.resourceable.created_at);
});

const formattedUrl = computed(() => {
    if(props.resource.resourceable.url.includes('http://') || props.resource.resourceable.url.includes('https://')) {
        return props.resource.resourceable.url;
    }
    return `https://${props.resource.resourceable.url}`;
})

const processedHtml = computed(() => {
    if (props.resource.resourceable.details === null) {
        return '';
    }

    let doc = new DOMParser().parseFromString(props.resource.resourceable.details, 'text/html');

    // Remove all <style> elements
    const styles = doc.getElementsByTagName('style');
    for (let i = styles.length - 1; i >= 0; i--) {
        styles[i].parentNode.removeChild(styles[i]);
    }

    // Return only the text content
    return doc.body.textContent || "";
});

const hasTags = computed(() => {
    return props.resource.tags.length > 0;
})

const hasUrl = computed(() => {
    return Object.hasOwn(props.resource.resourceable, 'url');
})

const isPageResource = computed(() => {
    return props.resource.resourceable_type === 'page';
})
</script>